import React, { useState } from "react";
import { Cell, Pie, PieChart, ResponsiveContainer } from "recharts";
import SocChartCard from "./SocChartCard";
import { STATUS_STYLES } from "./SocTile";
import { SOC_TILES } from "./socTileConfig";
import { areaIssueCount } from "./socConsole";
import { AREA_COLOR, NEUTRAL, SERIES, SEVERITY } from "./socChartTheme";

// Overview charts for Security Operations (/soc), built from the summary response only.

const isConnected = (tile) => !!tile && tile.status !== "notConnected" && tile.connected !== false;
const metric = (tile, key) => Number(tile?.metrics?.[key]) || 0;

const SEVERITIES = [
  ["critical", "Critical"],
  ["high", "High"],
  ["medium", "Medium"],
  ["low", "Low"],
];

// Pointing at a slice, or at (or focusing) its legend entry, pulls that slice out, fades the others
// and turns the centre into that severity's count and share.
export const SocSeverityDonut = ({ tile, emptyText = "No open incidents or alerts." }) => {
  const [active, setActive] = useState(null); // severity key | null
  const data = SEVERITIES.map(([key, label]) => ({
    key,
    label,
    value: metric(tile, key),
    color: SEVERITY[key],
  }));
  const total = data.reduce((sum, d) => sum + d.value, 0);
  const slices = data.filter((d) => d.value > 0);
  const on = data.find((d) => d.key === active) || null;

  return (
    <SocChartCard
      testId="soc-chart-severity"
      title="Incidents by severity"
      subtitle="Open Defender XDR incidents and alerts"
      empty={total === 0}
      emptyText={emptyText}
      legend={data.map(({ label, color }) => ({ label, color }))}
      activeLegend={on?.label ?? null}
      onLegendHover={(label) => setActive(data.find((d) => d.label === label)?.key ?? null)}
      table={{ columns: ["Severity", "Open"], rows: data.map((d) => [d.label, d.value]) }}
    >
      <div className="relative h-full" onMouseLeave={() => setActive(null)} data-testid="soc-severity-ring">
        <ResponsiveContainer width="100%" height="100%">
          <PieChart>
            {/* Zero-count slices stay in the legend and table, not in the ring. */}
            <Pie
              data={slices}
              dataKey="value"
              nameKey="label"
              innerRadius="60%"
              outerRadius="85%"
              paddingAngle={2}
              stroke="var(--bg-card-solid)"
              isAnimationActive={false}
              onMouseEnter={(_, i) => setActive(slices[i]?.key ?? null)}
            >
              {slices.map((d) => (
                <Cell key={d.key} fill={d.color} fillOpacity={active && active !== d.key ? 0.3 : 1}
                  className="cursor-pointer transition-[fill-opacity] duration-150" />
              ))}
            </Pie>
            {/* The pointed slice again, wider, over the ring: same angles, every other slice invisible. */}
            {on && on.value > 0 && (
              <Pie
                data={slices}
                dataKey="value"
                nameKey="label"
                innerRadius="57%"
                outerRadius="92%"
                paddingAngle={2}
                stroke="var(--bg-card-solid)"
                isAnimationActive={false}
                onMouseEnter={(_, i) => setActive(slices[i]?.key ?? null)}
              >
                {slices.map((d) => (
                  <Cell key={d.key} fill={d.color} fillOpacity={d.key === active ? 1 : 0}
                    strokeOpacity={d.key === active ? 1 : 0} data-testid={d.key === active ? "soc-severity-pop" : undefined}
                    style={d.key === active ? { filter: "drop-shadow(0 2px 4px rgba(15, 23, 42, 0.3))" } : undefined} />
                ))}
              </Pie>
            )}
          </PieChart>
        </ResponsiveContainer>
        <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center" aria-live="polite">
          {on ? (
            <>
              <span data-testid="soc-severity-readout" className="text-2xl font-bold tabular-nums" style={{ color: on.color }}>
                {on.value}
              </span>
              <span className="text-xs font-semibold text-content-primary">{on.label}</span>
              <span className="text-[11px] text-content-muted">
                {total > 0 ? `${Math.round((on.value / total) * 100)}% of ${total}` : ""}
              </span>
            </>
          ) : (
            <>
              <span data-testid="soc-severity-total" className="text-2xl font-bold text-content-primary">
                {total}
              </span>
              <span className="text-xs text-content-muted">open</span>
            </>
          )}
        </div>
      </div>
    </SocChartCard>
  );
};

const TILE_ORDER = Object.keys(SOC_TILES);

// One bar per connected area that reported its open issues (areaIssueCount, the figure its tile leads
// with). An area that reported none of its issue figures is left out rather than drawn as 0, since its
// tile then leads with another figure.
export const areaIssues = (tiles) =>
  (tiles || [])
    .filter((t) => isConnected(t) && areaIssueCount(t) != null)
    .map((t) => ({
      key: t.key,
      label: SOC_TILES[t.key].title,
      value: areaIssueCount(t) ?? 0,
      status: t.status,
    }))
    .sort((a, b) => b.value - a.value || TILE_ORDER.indexOf(a.key) - TILE_ORDER.indexOf(b.key));

// A polar-area chart: one equal-angle sector per area, its length to one linear radial scale (with
// rings at a quarter, half, three quarters and the largest value), coloured by area. Zero-value areas
// keep their legend row and draw no sector.
const R = 92;
const CX = 100;
const CY = 100;
const sector = (start, end, r) => {
  const x = (a) => CX + r * Math.sin(a);
  const y = (a) => CY - r * Math.cos(a);
  const large = end - start > Math.PI ? 1 : 0;
  return `M${CX},${CY} L${x(start).toFixed(2)},${y(start).toFixed(2)} A${r},${r} 0 ${large} 1 ${x(end).toFixed(2)},${y(end).toFixed(2)} Z`;
};

// Pointing at (or focusing) a sector or its legend row pulls that sector out, fades the others,
// lights the legend row and names it in the readout above the legend.
export const SocIssuesByArea = ({ tiles }) => {
  const [active, setActive] = useState(null); // area key | null
  const data = areaIssues(tiles);
  const max = Math.max(1, ...data.map((d) => d.value));
  const total = data.reduce((s, d) => s + d.value, 0);
  const step = (2 * Math.PI) / Math.max(data.length, 1);
  const gap = data.length > 1 ? 0.025 : 0;
  const activeRow = data.find((d) => d.key === active) || null;

  return (
    <SocChartCard
      testId="soc-chart-areas"
      title="Open issues by area"
      subtitle="Connected areas only"
      empty={data.length === 0}
      emptyText="No connected areas yet."
      table={{
        columns: ["Area", "Open issues", "Status"],
        rows: data.map((d) => [d.label, d.value, STATUS_STYLES[d.status]?.label ?? ""]),
      }}
    >
      <div className="flex h-full items-center gap-3" onMouseLeave={() => setActive(null)}>
        <svg viewBox="-8 -8 216 216" className="h-full max-h-full w-auto shrink-0 overflow-visible" role="img" aria-label="Open issues by area">
          {[0.25, 0.5, 0.75, 1].map((f) => (
            <circle key={f} cx={CX} cy={CY} r={R * f} fill="none" stroke="var(--chart-grid)" strokeWidth="1" />
          ))}
          {data.map((d, i) => {
            if (d.value <= 0) return null;
            const on = d.key === active;
            const mid = (i + 0.5) * step;
            // The pointed sector moves out along its own middle and grows a little.
            const dx = on ? 6 * Math.sin(mid) : 0;
            const dy = on ? -6 * Math.cos(mid) : 0;
            return (
              <path
                key={d.key}
                data-testid={`soc-area-sector-${d.key}`}
                d={sector(i * step + gap, (i + 1) * step - gap, Math.max(6, (d.value / max) * R) + (on ? 3 : 0))}
                transform={`translate(${dx.toFixed(2)} ${dy.toFixed(2)})`}
                fill={AREA_COLOR[d.key] || NEUTRAL}
                fillOpacity={on ? 1 : active ? 0.3 : 0.85}
                stroke="var(--bg-card-solid)"
                strokeWidth="1.5"
                className="cursor-pointer transition-[fill-opacity,transform] duration-150"
                style={on ? { filter: "drop-shadow(0 2px 4px rgba(15, 23, 42, 0.25))" } : undefined}
                onMouseEnter={() => setActive(d.key)}
              >
                <title>{`${d.label}: ${d.value}`}</title>
              </path>
            );
          })}
        </svg>
        <div className="min-w-0 flex-1">
          <p className="mb-1.5 min-h-[2rem] text-xs" data-testid="soc-areas-readout" aria-live="polite">
            {activeRow ? (
              <>
                <span className="block truncate font-semibold text-content-primary">{activeRow.label}</span>
                <span className="text-content-secondary">
                  {`${activeRow.value} open ${activeRow.value === 1 ? "issue" : "issues"}`}
                  {total > 0 && ` · ${Math.round((activeRow.value / total) * 100)}% of ${total}`}
                </span>
              </>
            ) : (
              <span className="text-content-muted">{`${total} open in total · point at an area for details`}</span>
            )}
          </p>
          <ul className="space-y-0.5 text-xs" aria-label="Areas">
            {data.map((d) => {
              const on = d.key === active;
              return (
                <li
                  key={d.key}
                  tabIndex={0}
                  data-testid={`soc-area-legend-${d.key}`}
                  onMouseEnter={() => setActive(d.key)}
                  onFocus={() => setActive(d.key)}
                  onBlur={() => setActive(null)}
                  className={`flex cursor-default items-center gap-2 rounded px-1 py-0.5 transition-colors focus:outline-none ${
                    on ? "bg-theme-hover" : active ? "opacity-50" : ""
                  }`}
                >
                  <span className={`shrink-0 rounded-sm transition-transform ${on ? "scale-125" : ""}`}
                    style={{ background: AREA_COLOR[d.key] || NEUTRAL, width: 10, height: 10 }} />
                  <span className={`truncate ${on ? "font-semibold text-content-primary" : "text-content-secondary"}`}>{d.label}</span>
                  <span className="ml-auto font-semibold tabular-nums text-content-primary">{d.value}</span>
                </li>
              );
            })}
          </ul>
        </div>
      </div>
    </SocChartCard>
  );
};

// A small decorative ring for the Connected areas KPI card; the card's text carries the numbers.
export const SocConnectedRing = ({ connected, total }) => {
  const data = [
    { key: "connected", value: Math.max(connected || 0, 0), color: SERIES[0] },
    { key: "notConnected", value: Math.max((total || 0) - (connected || 0), 0), color: NEUTRAL },
  ];

  return (
    <div data-testid="soc-connected-ring" aria-hidden="true" className="shrink-0" style={{ width: 56, height: 56 }}>
      <PieChart width={56} height={56}>
        <Pie
          data={data}
          dataKey="value"
          innerRadius={18}
          outerRadius={26}
          startAngle={90}
          endAngle={-270}
          stroke="none"
          isAnimationActive={false}
        >
          {data.map((d) => (
            <Cell key={d.key} fill={d.color} />
          ))}
        </Pie>
      </PieChart>
    </div>
  );
};
